import { ArrowDown, ArrowUp, Book, CalendarDays, CheckCircle2, FileText, Home, Paperclip, Star, Tag, Trash2 } from "lucide-react";
import { useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { Notebook } from "@engram/core";
import { createNote, defaultNotebookId, getSetting, setSetting } from "../db/actions";
import { db, useQuery } from "../db/client";
import { formatDate } from "./editor/suggest";
import { collator } from "./format";
import { useNesting } from "./notebookTree";

/*
 * Customizable sidebar (desktop) and navigation rail (phones), like Evernote's "Customize sidebar":
 * show, hide and reorder items. Synced settings `nav.sidebar` / `nav.rail` ({ order, hidden }), so every
 * device of the same kind looks the same. Also Daily notes: "Today" opens (or makes) today's note.
 */

export type NavKind = "sidebar" | "rail";
export type NavId = "home" | "shortcuts" | "notes" | "tasks" | "files" | "today" | "notebooks" | "tags" | "trash";

export const NAV_LABEL: Record<NavId, string> = {
  home: "Home",
  shortcuts: "Shortcuts",
  notes: "Notes",
  tasks: "Tasks",
  files: "Files",
  today: "Today's note",
  notebooks: "Notebooks",
  tags: "Tags",
  trash: "Trash",
};

export function navIcon(id: NavId, size: number): ReactNode {
  switch (id) {
    case "home": return <Home size={size} />;
    case "shortcuts": return <Star size={size} />;
    case "notes": return <FileText size={size} />;
    case "tasks": return <CheckCircle2 size={size} />;
    case "files": return <Paperclip size={size} />;
    case "today": return <CalendarDays size={size} />;
    case "notebooks": return <Book size={size} />;
    case "tags": return <Tag size={size} />;
    case "trash": return <Trash2 size={size} />;
  }
}

/** Evernote's defaults. The sidebar keeps Trash under More; Files isn't on Evernote Android's rail. "Today" starts hidden. */
const DEFAULTS: Record<NavKind, { order: NavId[]; hidden: NavId[] }> = {
  sidebar: { order: ["home", "shortcuts", "notes", "tasks", "files", "today", "notebooks", "tags"], hidden: ["today"] },
  rail: { order: ["home", "shortcuts", "notes", "tasks", "today", "files", "notebooks", "tags", "trash"], hidden: ["today", "files"] },
};

export interface NavConfig {
  order: NavId[];
  hidden: NavId[];
}

function normalize(kind: NavKind, raw: Partial<NavConfig> | undefined): NavConfig {
  const def = DEFAULTS[kind];
  const known = new Set(def.order);
  const order = (raw?.order ?? []).filter((x): x is NavId => known.has(x as NavId));
  for (const id of def.order) if (!order.includes(id)) order.push(id); // items added in later versions
  const hidden = raw?.hidden ? raw.hidden.filter((x): x is NavId => known.has(x as NavId)) : def.hidden;
  return { order, hidden };
}

/** The visible items, in order. */
export function useNav(kind: NavKind): NavId[] {
  const row = useQuery<{ value: string }>("SELECT value FROM settings WHERE id = ? AND deleted = 0", [`nav.${kind}`], ["settings"])?.[0];
  let raw: Partial<NavConfig> | undefined;
  try {
    raw = row ? (JSON.parse(row.value) as Partial<NavConfig>) : undefined;
  } catch {
    raw = undefined;
  }
  const c = normalize(kind, raw);
  return c.order.filter((id) => !c.hidden.includes(id));
}

/** Today's note: the note titled with today's date in the daily-notes notebook, made if missing. */
export async function openTodayNote(navigate: (to: string) => void) {
  const nb = (await getSetting<string>("daily.notebook")) ?? (await defaultNotebookId());
  const title = formatDate();
  const hit = await db.get<{ id: string }>(
    "SELECT id FROM notes WHERE notebook_id = ? AND title = ? AND deleted = 0 AND trashed_at IS NULL ORDER BY created LIMIT 1",
    [nb, title],
  );
  const id = hit?.id ?? (await createNote({ notebookId: nb, title }));
  navigate(`/notes?n=${id}`);
}

/** Off by default (Evernote has two levels): lets a stack go inside another stack. */
function NestingSwitch() {
  const on = useNesting();
  return (
    <label className="nav-config-switch">
      <input type="checkbox" checked={on} onChange={() => void setSetting("notebooks.nesting", !on)} />
      <span>Allow stacks inside stacks</span>
    </label>
  );
}

export function CustomizeNav({ kind, onClose }: { kind: NavKind; onClose: () => void }) {
  const row = useQuery<{ value: string }>("SELECT value FROM settings WHERE id = ? AND deleted = 0", [`nav.${kind}`], ["settings"])?.[0];
  const saved = normalize(kind, row ? (JSON.parse(row.value) as Partial<NavConfig>) : undefined);
  const [cfg, setCfg] = useState<NavConfig | null>(null);
  const c = cfg ?? saved;
  const notebooks = useQuery<Notebook>("SELECT * FROM notebooks WHERE deleted = 0 AND kind = 'notebook'", [], ["notebooks"]);
  const dailyRow = useQuery<{ value: string }>("SELECT value FROM settings WHERE id = 'daily.notebook' AND deleted = 0", [], ["settings"])?.[0];
  const defRow = useQuery<{ value: string }>("SELECT value FROM settings WHERE id = 'defaultNotebook' AND deleted = 0", [], ["settings"])?.[0];
  const daily = dailyRow ? (JSON.parse(dailyRow.value) as string) : defRow ? (JSON.parse(defRow.value) as string) : "";
  const move = (i: number, d: -1 | 1) => {
    const order = [...c.order];
    const j = i + d;
    if (j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j]!, order[i]!];
    setCfg({ ...c, order });
  };
  const toggle = (id: NavId) => setCfg({ ...c, hidden: c.hidden.includes(id) ? c.hidden.filter((x) => x !== id) : [...c.hidden, id] });
  return createPortal(
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal customize-nav" onKeyDown={(e) => e.key === "Escape" && onClose()}>
        <h2>{kind === "sidebar" ? "Customize sidebar" : "Customize navigation"}</h2>
        <p className="muted">Show, hide and reorder items. {kind === "sidebar" ? "Trash stays under More." : ""}</p>
        <ul className="nav-config">
          {c.order.map((id, i) => (
            <li key={id} className={c.hidden.includes(id) ? "off" : ""}>
              <label>
                <input type="checkbox" checked={!c.hidden.includes(id)} onChange={() => toggle(id)} />
                <span className="nav-config-icon">{navIcon(id, 16)}</span>
                <span>{NAV_LABEL[id]}</span>
              </label>
              <button className="icon-btn" title="Move up" aria-label={`Move ${NAV_LABEL[id]} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                <ArrowUp size={14} />
              </button>
              <button className="icon-btn" title="Move down" aria-label={`Move ${NAV_LABEL[id]} down`} disabled={i === c.order.length - 1} onClick={() => move(i, 1)}>
                <ArrowDown size={14} />
              </button>
            </li>
          ))}
        </ul>
        <label className="modal-label">Today's note goes in</label>
        <select className="input" value={daily} onChange={(e) => void setSetting("daily.notebook", e.target.value)}>
          {(notebooks ?? [])
            .slice()
            .sort((a, b) => collator.compare(a.name, b.name))
            .map((nb) => (
              <option key={nb.id} value={nb.id}>
                {nb.name}
              </option>
            ))}
        </select>
        {kind === "sidebar" && <NestingSwitch />}
        <p className="muted small">Today's note: Ctrl+Alt+D. It's titled with the date, e.g. "{formatDate()}".</p>
        <div className="modal-actions">
          <button className="btn" onClick={() => setCfg(normalize(kind, undefined))}>
            Reset
          </button>
          <span className="sketch-gap" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            onClick={() => {
              void setSetting(`nav.${kind}`, c);
              onClose();
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
